import { useState, useEffect } from "react";
import axios from "axios";
import { Sparkles, ArrowRight, ArrowLeft, Loader2, SkipForward, Lock, Pin } from "lucide-react";
import { toast } from "sonner";
import PremiumGate, { usePremium } from "@/components/PremiumGate";
import { useAuth } from "@/contexts/AuthContext";
import DailyLogPanel from "@/components/DailyLogPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FREE_USES_KEY = "omnimuscle_ai_free_uses_v1";
const FREE_LIMIT = 1; // 1 free AI plan per week for non-premium (mirrors server)

function getFreeUsesThisWeek() {
  try {
    const raw = localStorage.getItem(FREE_USES_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return arr.filter((ts) => ts > weekAgo);
  } catch {
    return [];
  }
}

function recordFreeUse() {
  try {
    const arr = getFreeUsesThisWeek();
    arr.push(Date.now());
    localStorage.setItem(FREE_USES_KEY, JSON.stringify(arr));
  } catch {
    // ignore
  }
}

/* ---------- Step config (multi-field steps supported) ----------
   - required: true  → at least one required field on the step must be filled to continue
   - optional: true  → step can be skipped entirely
*/
const STEPS = [
  {
    id: "name",
    title: "What's your name?",
    fields: [
      { key: "name", type: "text", placeholder: "e.g., Roc", required: true },
    ],
    required: true,
  },
  {
    id: "vitals",
    title: "Your Vitals",
    subtitle: "Sex, age, and weight are required for accurate calorie & macro math. Height is optional.",
    fields: [
      {
        key: "sex",
        label: "Sex",
        type: "select",
        required: true,
        options: [
          { v: "male", l: "Male" },
          { v: "female", l: "Female" },
          { v: "other", l: "Prefer not to say" },
        ],
      },
      { key: "age", label: "Age", type: "text", placeholder: "e.g., 32", required: true },
      {
        key: "weight",
        label: "Current Weight",
        type: "text",
        placeholder: "e.g., 185 lb or 84 kg",
        required: true,
      },
      {
        key: "height",
        label: "Height (optional)",
        type: "text",
        placeholder: 'e.g., 5\'10" or 178cm',
      },
    ],
  },
  {
    id: "body-comp",
    title: "Body Composition",
    subtitle: "Optional — leave blank if unknown.",
    optional: true,
    fields: [
      {
        key: "body_fat_pct",
        label: "Body Fat %",
        type: "text",
        placeholder: "e.g., 14",
      },
      {
        key: "goal_weight",
        label: "Goal Weight",
        type: "text",
        placeholder: "e.g., 200 lb",
      },
      {
        key: "weight_trend",
        label: "Recent Weight Trend",
        type: "select",
        options: [
          { v: "dropping", l: "Dropping" },
          { v: "holding", l: "Holding steady" },
          { v: "gaining", l: "Gaining" },
        ],
      },
    ],
  },
  {
    id: "measurements",
    title: "Measurements",
    subtitle: "Optional — useful for tracking symmetry & progress.",
    optional: true,
    fields: [
      {
        key: "waist",
        label: "Waist",
        type: "text",
        placeholder: "e.g., 32 in or 81 cm",
      },
      {
        key: "chest",
        label: "Chest",
        type: "text",
        placeholder: "e.g., 44 in or 112 cm",
      },
    ],
  },
  {
    id: "goal",
    title: "What's the primary goal?",
    required: true,
    fields: [
      {
        key: "goal",
        type: "select",
        required: true,
        options: [
          { v: "build_muscle", l: "Build Muscle (Hypertrophy)" },
          { v: "fat_loss", l: "Fat Loss / Cutting" },
          { v: "strength", l: "Raw Strength" },
          { v: "athletic_performance", l: "Athletic Performance" },
          { v: "stage_prep", l: "Stage / Show Prep" },
        ],
      },
    ],
  },
  {
    id: "training",
    title: "Training Profile",
    subtitle: "These three shape your split.",
    required: true,
    fields: [
      {
        key: "experience",
        label: "Experience",
        type: "select",
        required: true,
        options: [
          { v: "beginner", l: "Beginner (0–1 yrs)" },
          { v: "intermediate", l: "Intermediate (1–3 yrs)" },
          { v: "advanced", l: "Advanced (3+ yrs)" },
        ],
      },
      {
        key: "body_type",
        label: "Body Type",
        type: "select",
        required: true,
        options: [
          { v: "ectomorph", l: "Ectomorph (lean / hard gainer)" },
          { v: "mesomorph", l: "Mesomorph (athletic build)" },
          { v: "endomorph", l: "Endomorph (holds bodyfat easily)" },
        ],
      },
      {
        key: "days_per_week",
        label: "Days per week",
        type: "select",
        required: true,
        options: [
          { v: 3, l: "3 days" },
          { v: 4, l: "4 days" },
          { v: 5, l: "5 days" },
          { v: 6, l: "6 days" },
        ],
      },
    ],
  },
  {
    id: "lifestyle",
    title: "Lifestyle",
    subtitle: "Optional — recovery is built outside the gym.",
    optional: true,
    fields: [
      {
        key: "activity_outside_gym",
        label: "Activity outside gym",
        type: "select",
        options: [
          { v: "sedentary", l: "Sedentary (desk job)" },
          { v: "lightly_active", l: "Lightly active" },
          { v: "on_feet", l: "On my feet most of the day" },
          { v: "very_physical", l: "Very physical job / labor" },
        ],
      },
      {
        key: "sleep_hours",
        label: "Average sleep",
        type: "select",
        options: [
          { v: "<5", l: "Less than 5 hrs" },
          { v: "5-6", l: "5–6 hrs" },
          { v: "6-7", l: "6–7 hrs" },
          { v: "7-8", l: "7–8 hrs" },
          { v: "8+", l: "8+ hrs" },
        ],
      },
      {
        key: "stress_level",
        label: "Stress level",
        type: "select",
        options: [
          { v: "low", l: "Low" },
          { v: "moderate", l: "Moderate" },
          { v: "high", l: "High" },
          { v: "severe", l: "Severe / burnout" },
        ],
      },
    ],
  },
  {
    id: "nutrition",
    title: "Nutrition",
    subtitle: "Optional — helps me dial macros to your reality.",
    optional: true,
    fields: [
      {
        key: "calorie_tracking_exp",
        label: "Calorie tracking experience",
        type: "select",
        options: [
          { v: "never", l: "Never tracked" },
          { v: "tried", l: "Tried it" },
          { v: "currently", l: "Currently track" },
          { v: "expert", l: "Track every meal" },
        ],
      },
      {
        key: "dietary_restrictions",
        label: "Dietary restrictions",
        type: "text",
        placeholder: "e.g., no dairy, vegetarian, allergic to shellfish",
      },
    ],
  },
  {
    id: "notes",
    title: "Anything else I should know?",
    subtitle: "Injuries, equipment limits, focus areas — optional.",
    optional: true,
    fields: [
      {
        key: "notes",
        type: "textarea",
        placeholder: "Optional",
      },
    ],
  },
];

const INITIAL = {
  name: "",
  sex: "",
  age: "",
  height: "",
  weight: "",
  body_fat_pct: "",
  goal_weight: "",
  weight_trend: "",
  waist: "",
  chest: "",
  goal: "",
  experience: "",
  body_type: "",
  days_per_week: 4,
  activity_outside_gym: "",
  sleep_hours: "",
  stress_level: "",
  calorie_tracking_exp: "",
  dietary_restrictions: "",
  notes: "",
};

/* ---------- Markdown helpers ---------- */
function renderMarkdown(md) {
  // Defensive: handle null/undefined/non-string plan payloads gracefully.
  if (!md || typeof md !== "string") {
    return '<p style="color:#EF4444">⚠️ The plan came back empty. Try again, and if it keeps happening, refresh the page.</p>';
  }
  try {
    const lines = md.split("\n");
    let html = "";
    let inTable = false;
    let inList = false;
    let tableRows = [];

    const flushList = () => {
      if (inList) {
        html += "</ul>";
        inList = false;
      }
    };
    const flushTable = () => {
      if (inTable && tableRows.length) {
        html += "<table><thead>";
        html += "<tr>" + tableRows[0].map((c) => `<th>${c}</th>`).join("") + "</tr>";
        html += "</thead><tbody>";
        tableRows.slice(1).forEach((row) => {
          html += "<tr>" + row.map((c) => `<td>${c}</td>`).join("") + "</tr>";
        });
        html += "</tbody></table>";
      }
      inTable = false;
      tableRows = [];
    };

    lines.forEach((line) => {
      const trimmed = line.trim();
      if (trimmed.startsWith("### ")) {
        flushList(); flushTable();
        html += `<h3>${formatInline(trimmed.slice(4))}</h3>`;
      } else if (trimmed.startsWith("## ")) {
        flushList(); flushTable();
        html += `<h2>${formatInline(trimmed.slice(3))}</h2>`;
      } else if (trimmed.startsWith("# ")) {
        // Treat single-# headings as h2 (some LLMs emit single # despite prompt)
        flushList(); flushTable();
        html += `<h2>${formatInline(trimmed.slice(2))}</h2>`;
      } else if (/^-{3,}$/.test(trimmed) || /^\*{3,}$/.test(trimmed)) {
        flushList(); flushTable();
        html += "<hr/>";
      } else if (trimmed.startsWith("|") && trimmed.endsWith("|")) {
        const cells = trimmed.slice(1, -1).split("|").map((c) => c.trim());
        if (cells.every((c) => /^[-:]+$/.test(c))) return;
        if (!inTable) {
          flushList();
          inTable = true;
        }
        tableRows.push(cells);
      } else if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
        flushTable();
        if (!inList) {
          html += "<ul>";
          inList = true;
        }
        html += `<li>${formatInline(trimmed.slice(2))}</li>`;
      } else if (trimmed === "") {
        flushList(); flushTable();
      } else {
        flushList(); flushTable();
        html += `<p>${formatInline(trimmed)}</p>`;
      }
    });
    flushList();
    flushTable();
    return html;
  } catch (err) {
    // If anything breaks, fall back to a readable pre-block so the user still
    // gets the plan content — never show a blank page.
    const safe = String(md).replace(/[&<>]/g, (c) => ({"&":"&amp;","<":"&lt;",">":"&gt;"}[c]));
    return `<pre style="white-space:pre-wrap;font-family:inherit;line-height:1.6">${safe}</pre>`;
  }
}
function formatInline(text) {
  return text
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/`(.+?)`/g, "<code>$1</code>");
}

/* ---------- Component ---------- */
export default function AiCoach() {
  const { logActivity, user } = useAuth();
  const [step, setStep] = useState(0);
  const [data, setData] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState("");
  const [showGate, setShowGate] = useState(false);
  const isPremium = usePremium();
  const [serverQuota, setServerQuota] = useState(null);
  const [prefillNotice, setPrefillNotice] = useState(null); // shows which fields were auto-filled
  const [lastPlanInfo, setLastPlanInfo] = useState(null);   // { timestamp, plan_id } for "last plan" chip

  // 🪄 Auto-prefill from the user's profile so they only have to fill the gaps.
  // Pulls from: profile (name/sex/age/height/weight/goal) + latest vitals
  // (weight_trend) + AI-Coach last submission (if any) for the heavy fields.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const filled = {};
      const filledLabels = [];
      const safe = (v) => (v == null ? "" : String(v));
      // Map directly from the user object (cheapest)
      const map = [
        ["name",   safe(user.name),   "Name"],
        ["sex",    safe(user.sex),    "Sex"],
        ["age",    safe(user.age),    "Age"],
        ["height", safe(user.height), "Height"],
        ["weight", safe(user.weight), "Weight"],
        ["goal",   safe(user.goal),   "Goal"],
      ];
      for (const [k, v, label] of map) {
        if (v && !INITIAL[k]) {
          filled[k] = v;
          filledLabels.push(label);
        } else if (v) {
          filled[k] = v;
          filledLabels.push(label);
        }
      }
      // Last AI-Coach submission — reuse experience, body_type, days_per_week,
      // activity_outside_gym, sleep_hours, stress_level, dietary_restrictions
      try {
        const { data: hist } = await axios.get(`${API}/sync/ai-plans`);
        const last = hist?.plans?.[0]?.input;
        const lastTs = hist?.plans?.[0]?.timestamp;
        if (lastTs && !cancelled) {
          setLastPlanInfo({ timestamp: lastTs, plan_id: hist.plans[0].id });
        }
        if (last) {
          const carryOver = [
            ["body_fat_pct", "Body fat %"],
            ["goal_weight", "Goal weight"],
            ["waist", "Waist"],
            ["chest", "Chest"],
            ["experience", "Experience"],
            ["body_type", "Body type"],
            ["days_per_week", "Days/week"],
            ["activity_outside_gym", "Outside activity"],
            ["sleep_hours", "Sleep"],
            ["stress_level", "Stress"],
            ["calorie_tracking_exp", "Calorie tracking"],
            ["dietary_restrictions", "Diet"],
            ["notes", "Notes"],
          ];
          for (const [k, label] of carryOver) {
            const v = last[k];
            if (v != null && v !== "" && !filled[k]) {
              filled[k] = v;
              filledLabels.push(label);
            }
          }
        }
      } catch {
        // No prior plan or 401 — skip silently
      }
      if (cancelled) return;
      if (Object.keys(filled).length > 0) {
        setData((d) => ({ ...d, ...filled }));
        setPrefillNotice({ count: Object.keys(filled).length, labels: filledLabels });
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Refresh server-side quota whenever auth state flips
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: q } = await axios.get(`${API}/recommend/quota`);
        if (!cancelled) setServerQuota(q);
      } catch {
        if (!cancelled) setServerQuota(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, isPremium]);

  // Prefer server count over the legacy localStorage one when authenticated
  const localUses = getFreeUsesThisWeek().length;
  const usesThisWeek = serverQuota && serverQuota.authenticated
    ? serverQuota.used_this_week
    : localUses;
  const remainingFree = Math.max(0, FREE_LIMIT - usesThisWeek);
  const isLocked = !isPremium && remainingFree === 0;

  const total = STEPS.length;
  const progress = ((step + 1) / total) * 100;
  const current = STEPS[step];

  const setVal = (key, v) => setData((d) => ({ ...d, [key]: v }));

  // Required-field gate: every required field on this step must be non-empty.
  const canContinue = () => {
    const req = current.fields.filter((f) => f.required);
    if (req.length === 0) return true;
    return req.every((f) => {
      const v = data[f.key];
      return v !== "" && v !== null && v !== undefined;
    });
  };

  const next = () => {
    if (step < total - 1) setStep((s) => s + 1);
    else submit();
  };
  const back = () => step > 0 && setStep((s) => s - 1);

  const skip = () => {
    // Reset only this step's optional fields to their initial values, then advance
    if (current.optional) {
      const reset = {};
      current.fields.forEach((f) => (reset[f.key] = INITIAL[f.key]));
      setData((d) => ({ ...d, ...reset }));
    }
    next();
  };

  const submit = async () => {
    // Frontend pre-check (server is the real gate, but this avoids a wasted POST)
    if (!user) {
      // Not logged in — show the gate so they can see plans & log in via top-right
      setShowGate(true);
      return;
    }
    if (!isPremium && usesThisWeek >= FREE_LIMIT) {
      setShowGate(true);
      return;
    }
    setLoading(true);
    setError("");
    try {
      // Coerce age to int if provided
      const payload = {
        ...data,
        age: data.age ? parseInt(data.age, 10) || null : null,
      };
      const res = await axios.post(`${API}/recommend`, payload);
      const planText = res?.data?.plan;
      if (!planText || typeof planText !== "string" || !planText.trim()) {
        setError("The AI returned an empty response. Please try again in a moment.");
        return;
      }
      setPlan(planText);
      if (!isPremium) recordFreeUse();
      // Refresh server quota so the banner updates
      try {
        const { data: q } = await axios.get(`${API}/recommend/quota`);
        setServerQuota(q);
      } catch {
        // ignore
      }
      logActivity?.("ai_plan");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      // Server-side paywall hit — open the gate, don't show as error
      if (status === 402) {
        setShowGate(true);
        return;
      }
      if (status === 401) {
        setShowGate(true);
        return;
      }
      const msg =
        (typeof detail === "string" && detail) ||
        detail?.message ||
        "Couldn't generate plan right now. Try again in a moment.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep(0);
    setPlan(null);
    setData(INITIAL);
  };

  const [pinning, setPinning] = useState(false);
  const [pinned, setPinned] = useState(false);
  const pinToProfile = async () => {
    if (!plan) return;
    if (pinned) {
      toast.info("This plan is already pinned to your profile.");
      return;
    }
    if (!window.confirm("Pin this AI Coach plan to your profile? It will replace any previously pinned plan.")) return;
    setPinning(true);
    try {
      await axios.post(`${API}/profile/pin`, {
        kind: "coach",
        data: {
          plan,
          goal: data.goal,
          level: data.level,
          age: data.age || null,
        },
      });
      setPinned(true);
      toast.success("📌 Pinned to your profile — visible on your Profile page.", { duration: 5000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pin failed.");
    } finally {
      setPinning(false);
    }
  };

  return (
    <section
      id="ai-coach"
      data-testid="ai-coach-section"
      className="py-24 md:py-36 bg-gradient-to-b from-[#0A0A0A] via-[#120824] to-[#0A0A0A] relative overflow-hidden"
    >
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#4C1D95] opacity-20 blur-[150px] rounded-full pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#D4AF37]/40 text-[#D4AF37] mb-6">
            <Sparkles size={14} />
            <span className="font-accent text-xs tracking-[0.3em] uppercase">
              AI Coach OMNI
            </span>
          </div>
          <h2 className="font-display text-5xl md:text-7xl text-white leading-[0.9] mb-4">
            Your <span className="text-[#D4AF37]">Personal</span>
            <br />
            Protocol — Generated Now.
          </h2>
          <p className="text-neutral-400 max-w-2xl mx-auto">
            Walk through 9 quick steps. Coach OMNI builds your training split,
            calorie & macro targets, recovery plan, and supplement stack —
            personalized to your full body composition.
          </p>
        </div>

        {/* 📊 Unified Daily Log — same log used by Kitchen, Gym, Tracker, Calendar */}
        <div className="mb-10" data-testid="aicoach-daily-log">
          <DailyLogPanel title="Coach OMNI · Today's Log" />
        </div>

        {!plan ? (
          <div
            data-testid="ai-coach-form"
            className="bg-[#0F0F0F] border border-zinc-800 p-8 md:p-12"
          >
            {/* Premium / free use status banner */}
            <div
              data-testid="ai-status-banner"
              className={`mb-6 px-4 py-3 border flex items-center justify-between gap-3 ${
                isPremium
                  ? "border-[#D4AF37] bg-[#D4AF37]/5"
                  : isLocked
                  ? "border-[#DC2626] bg-[#DC2626]/5"
                  : "border-zinc-800 bg-[#0A0A0A]"
              }`}
            >
              <div className="flex items-center gap-2 text-xs">
                {isPremium ? (
                  <>
                    <Sparkles size={14} className="text-[#D4AF37]" />
                    <span className="text-[#D4AF37] font-bold uppercase tracking-widest">
                      Premium · Unlimited Plans
                    </span>
                  </>
                ) : isLocked ? (
                  <>
                    <Lock size={14} className="text-[#DC2626]" />
                    <span className="text-[#EF4444] font-semibold">
                      Free weekly plan used. Upgrade for unlimited.
                    </span>
                  </>
                ) : (
                  <span className="text-neutral-400">
                    Free tier · 1 AI plan per week
                  </span>
                )}
              </div>
              {!isPremium && (
                <button
                  data-testid="ai-upgrade-btn"
                  onClick={() => setShowGate(true)}
                  className="text-[10px] uppercase tracking-widest font-bold text-[#D4AF37] hover:text-white"
                >
                  {isLocked ? "Unlock Premium" : "Go Premium"}
                </button>
              )}
            </div>

            <div className="flex items-center justify-between mb-6">
              <span className="font-accent text-xs uppercase tracking-widest text-neutral-500">
                Step {step + 1} of {total}
                {current.optional && (
                  <span className="ml-2 text-[#D4AF37]">· Optional</span>
                )}
              </span>
              <span className="font-display text-[#D4AF37]">
                {String(step + 1).padStart(2, "0")} /{" "}
                <span className="text-neutral-600">{String(total).padStart(2, "0")}</span>
              </span>
            </div>

            {/* 📅 Last-plan chip — shown only at step 0 when prior plan exists */}
            {step === 0 && lastPlanInfo && (
              <div
                data-testid="ai-coach-last-plan"
                className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 border border-zinc-800 bg-[#0A0A0A] text-xs"
              >
                <span className="text-neutral-500">📅 Last plan:</span>
                <span className="text-white font-mono tabular-nums">
                  {new Date(lastPlanInfo.timestamp).toLocaleDateString(undefined, {
                    month: "2-digit", day: "2-digit", year: "2-digit"
                  })}
                </span>
                {(() => {
                  const days = Math.floor((Date.now() - new Date(lastPlanInfo.timestamp)) / 86400000);
                  if (days >= 30) {
                    return <span className="text-[#D4AF37] font-bold uppercase tracking-widest">· stale · refresh recommended</span>;
                  }
                  if (days >= 14) {
                    return <span className="text-neutral-400">· {days}d ago</span>;
                  }
                  return <span className="text-neutral-500">· {days === 0 ? "today" : `${days}d ago`}</span>;
                })()}
              </div>
            )}

            <div className="progress-bar mb-10">
              <div
                className="progress-fill"
                style={{ transform: `scaleX(${progress / 100})` }}
              />
            </div>

            <h3 className="font-display text-3xl md:text-4xl text-white mb-2">
              {current.title}
            </h3>
            {current.subtitle && (
              <p className="text-neutral-400 text-sm md:text-base mb-4">
                {current.subtitle}
              </p>
            )}

            {/* 🪄 Auto-prefill banner — shown once at step 0 to set expectations */}
            {step === 0 && prefillNotice && prefillNotice.count > 0 && (
              <div
                data-testid="ai-coach-prefill-notice"
                className="mb-6 border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-4 py-3 flex items-start gap-3"
              >
                <span className="text-xl leading-none mt-0.5">🪄</span>
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] font-bold mb-1">
                    Auto-filled from your profile · {prefillNotice.count} field{prefillNotice.count === 1 ? "" : "s"}
                  </div>
                  <div className="text-xs text-neutral-300 leading-relaxed">
                    {prefillNotice.labels.slice(0, 8).join(" · ")}
                    {prefillNotice.labels.length > 8 ? ` +${prefillNotice.labels.length - 8} more` : ""}
                  </div>
                  <div className="text-[10px] text-neutral-500 mt-1.5">
                    Tap any field to adjust — only the empty ones need your input.
                  </div>
                </div>
                <button
                  data-testid="ai-coach-prefill-dismiss"
                  onClick={() => setPrefillNotice(null)}
                  className="text-neutral-500 hover:text-white text-xs leading-none"
                  title="Dismiss"
                >
                  ✕
                </button>
              </div>
            )}

            <div className="mb-10 space-y-6">
              {current.fields.map((field) => {
                const value = data[field.key];
                return (
                  <div key={field.key}>
                    {field.label && (
                      <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-2">
                        {field.label}
                        {field.required && (
                          <span className="text-[#DC2626] ml-1">*</span>
                        )}
                      </label>
                    )}

                    {field.type === "text" && (
                      <input
                        data-testid={`ai-input-${field.key}`}
                        className="input-omni"
                        placeholder={field.placeholder}
                        value={value || ""}
                        onChange={(e) => setVal(field.key, e.target.value)}
                      />
                    )}

                    {field.type === "textarea" && (
                      <textarea
                        data-testid={`ai-input-${field.key}`}
                        rows={4}
                        className="input-omni"
                        placeholder={field.placeholder}
                        value={value || ""}
                        onChange={(e) => setVal(field.key, e.target.value)}
                      />
                    )}

                    {field.type === "select" && (
                      <div className="grid sm:grid-cols-2 gap-2.5">
                        {field.options.map((opt) => {
                          const active = value === opt.v;
                          return (
                            <button
                              key={String(opt.v)}
                              data-testid={`ai-option-${field.key}-${opt.v}`}
                              onClick={() => setVal(field.key, opt.v)}
                              className={`text-left p-3.5 border-2 transition-all ${
                                active
                                  ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white"
                                  : "border-zinc-800 bg-[#0A0A0A] text-neutral-300 hover:border-zinc-600"
                              }`}
                            >
                              <span className="font-accent text-sm font-semibold">
                                {opt.l}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {error && (
              <div
                data-testid="ai-error"
                className="bg-[#DC2626]/10 border border-[#DC2626] text-[#EF4444] px-4 py-3 mb-6 text-sm"
              >
                {error}
              </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3">
              <button
                data-testid="ai-btn-back"
                onClick={back}
                disabled={step === 0 || loading}
                className="btn-omni btn-outline-gold disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ArrowLeft size={18} /> Back
              </button>

              <div className="flex flex-wrap items-center gap-3 ml-auto">
                {current.optional && (
                  <button
                    data-testid="ai-btn-skip"
                    onClick={skip}
                    disabled={loading}
                    className="btn-omni btn-outline-gold disabled:opacity-50"
                  >
                    Skip <SkipForward size={16} />
                  </button>
                )}
                <button
                  data-testid="ai-btn-next"
                  onClick={next}
                  disabled={!canContinue() || loading}
                  className="btn-omni btn-red disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> Generating Protocol…
                    </>
                  ) : step === total - 1 ? (
                    <>Generate My Plan <Sparkles size={18} /></>
                  ) : (
                    <>Next <ArrowRight size={18} /></>
                  )}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div data-testid="ai-coach-output" className="ai-output p-8 md:p-12 relative">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8 border-b border-zinc-800 pb-6">
              <div>
                <div className="text-[#D4AF37] font-accent text-xs uppercase tracking-[0.3em] mb-1">
                  Coach OMNI · Personalized
                </div>
                <h3 className="font-display text-3xl md:text-4xl text-white">
                  {data.name ? `${data.name}'s Protocol` : "Your Protocol"}
                </h3>
              </div>
              <div className="flex gap-2">
                <button
                  data-testid="ai-btn-pin"
                  onClick={pinToProfile}
                  disabled={pinning || pinned}
                  className="btn-omni btn-outline-gold disabled:opacity-60"
                  title="Pin this plan to your profile (overrides previous pin)"
                >
                  {pinning ? <Loader2 className="animate-spin" size={14} /> : <Pin size={14} />}
                  {pinned ? "Pinned" : "Pin to Profile"}
                </button>
                <button
                  data-testid="ai-btn-reset"
                  onClick={reset}
                  className="btn-omni btn-outline-gold"
                >
                  Build Another
                </button>
              </div>
            </div>
            <div
              className="ai-prose"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(plan) }}
            />
          </div>
        )}
      </div>

      <PremiumGate
        open={showGate}
        onClose={() => setShowGate(false)}
        feature="Unlimited AI Coach"
      />
    </section>
  );
}
